import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react"
import * as XLSX from "xlsx"
import { Button } from "@components/common/button"
import { Input } from "@components/common/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"
import { Label } from "@components/common/label"
import { RadioGroup, RadioGroupItem } from "@components/common/radio-group"
import useTranslator from "@pma/hooks/use-translator"
import { toast } from "@pma/hooks/use-toast"
import { ChevronDown, ChevronUp, Download, Pen, Upload, X } from "lucide-react"
import { cn } from "@pma/utils/helpers"
import { Badge } from "@components/common/badge"
import { Alert } from "@components/common/alert"
import { DictType } from "@pma/types"
import { Separator } from "@components/common/separator"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import axios from "axios"
import {
  FINANCIAL_SERVER_URL,
  MAPPING_ADD_ENDPOINT,
  MAPPING_GET_ENDPOINT,
} from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation, useQuery } from "@tanstack/react-query"

const expectedPLSchema = ["Number", "Unit", "Price", "Description"] as const
const expectedBoQSchema = [...expectedPLSchema, "Amount"] as const

interface Props {
  setMapping: Dispatch<SetStateAction<DictType>>
  mappingType: "bill" | "boq" | "price list"
}

export const PriceListExcelMapper = ({ setMapping, mappingType }: Props) => {
  const [file, setFile] = useState<FileList | undefined>(undefined)
  const [priceListName, setPriceListName] = useState("")
  const [isDragging, setIsDragging] = useState(false)
  const [previewExcel, setPreviewExcel] = useState(false)
  const [submittedMapping, setSubmittedMapping] = useState(false)
  const [sheetData, setSheetData] = useState<any[]>([])
  const [detectedColumns, setDetectedColumns] = useState<string[]>([])
  const [excelColumns, setExcelColumns] = useState<string[]>([])
  const [schemaValues, setSchemaValues] = useState<Record<string, string>>({})
  const [uploadType, setUploadType] = useState<"excel" | "skn">("excel")
  const [sknFileContent, setSknFileContent] = useState<string>("")

  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"

  const boq = mappingType === "boq"

  const expectedSchema = boq ? expectedBoQSchema : expectedPLSchema

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files) {
      if (e.dataTransfer?.files[0]) {
        const file: any = e.dataTransfer.files[0]

        if (uploadType === "excel") {
          if (
            file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            file.type === "application/vnd.ms-excel"
          ) {
            setFile(e.dataTransfer.files)
            onFileUpload(e, true)
          } else {
            toast({
              title: translate("Info"),
              description: translate("Invalid format. Please upload Excel files only"),
              variant: "info",
            })
            setFile(undefined)
          }
        } else {
          // Handle SKN file upload
          setFile(e.dataTransfer.files)
          onSknFileUpload(e, true)
        }
      }
    }
  }

  const onFileUpload = (e: ChangeEvent<HTMLInputElement> | any, drag?: boolean) => {
    const file = drag ? e.dataTransfer.files[0] : e.target.files?.[0]
    if (!file) return

    setPriceListName(file?.name.replace(".xlsx", "").replaceAll(".xls", ""))

    const reader = new FileReader()
    reader.onload = (evt) => {
      const bstr = evt.target?.result

      try {
        const workbook = XLSX.read(bstr, { type: "array" })
        const ws = workbook.Sheets[workbook.SheetNames[0]]

        // --- Fix: find the real header row ---
        const range = XLSX.utils.decode_range(ws["!ref"] || "A1")
        let headerRowIndex = 0

        // Scan rows to find the one with the most non-empty cells (the actual header)
        let maxNonEmpty = 0
        for (let r = range.s.r; r <= Math.min(range.s.r + 10, range.e.r); r++) {
          let nonEmptyCount = 0
          for (let c = range.s.c; c <= range.e.c; c++) {
            const cellAddress = XLSX.utils.encode_cell({ r, c })
            const cell = ws[cellAddress]
            if (cell && cell.v !== undefined && String(cell.v).trim() !== "") {
              nonEmptyCount++
            }
          }
          if (nonEmptyCount > maxNonEmpty) {
            maxNonEmpty = nonEmptyCount
            headerRowIndex = r
          }
        }

        // Parse with the correct header row
        const data = XLSX.utils.sheet_to_json(ws, {
          defval: "",
          range: headerRowIndex, // start reading from the detected header row
        })

        if (data.length > 0) {
          const cols = Object.keys(data[0] as object).filter(
            (col: any) => col !== null && col !== "" && !col.includes("__EMPTY"),
          )

          setExcelColumns(cols)
          setSheetData(data)
          setDetectedColumns(cols)
        }
      } catch (error: any) {
        toast({
          title: translate("Error"),
          description: translate(error?.message),
          variant: "error",
        })
        return error
      }
    }
    reader.readAsArrayBuffer(file)
  }
  const onSknFileUpload = async (e: ChangeEvent<HTMLInputElement> | any, drag?: boolean) => {
    const file = drag ? e.dataTransfer.files[0] : e.target.files?.[0]

    if (!file) return

    setPriceListName(file?.name.replace(/\.[^/.]+$/, "")) // Remove any file extension

    try {
      // Create FormData to send the SKN file to the API
      const formData = new FormData()
      formData.append("file", file)

      // Make API call to convert SKN to CSV
      const response = await axios.post(
        "https://xk6s-eqwg-s8by.f2.xano.io/api:0Dvy81hK:v2/service/skn-to-csv",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )

      // Check if response contains CSV data
      if (response.data && response.data.success && response.data.csv) {
        const csvData = response.data.csv

        // Create a blob from the CSV data
        const blob = new Blob([csvData], { type: "text/csv;charset=windows-1255" })
        const csvFile = new File([blob], `${file.name}_processed.csv`, { type: "text/csv" })

        // Use XLSX to parse the CSV data directly
        const workbook = XLSX.read(csvData, { type: "string" })
        const ws = workbook.Sheets[workbook.SheetNames[0]]
        const data = XLSX.utils.sheet_to_json(ws, { defval: "" })

        if (data.length > 0) {
          const cols = Object.keys(data[0] as object).filter(
            (col: any) => col !== null && col !== "",
          )

          setExcelColumns(cols)
          setSheetData(data)

          const tempColsFromKeys = data
            .map((keyValue: any) => {
              if (Object.keys(keyValue).filter((col) => col.includes("__EMPTY")).length < 3) {
                return Object.keys(keyValue)
              }
              return
            })
            .filter(Boolean)

          const tempColsFromValues = data
            .map((keyValue: any) => {
              return Object.values(keyValue).filter((value) => value && value !== "__EMPTY")
            })
            .find((values) => values.length >= 5)

          const usingColsFromKeys = tempColsFromKeys.length > 0

          const detectedCols = usingColsFromKeys
            ? (tempColsFromKeys.find(Boolean) as string[])
            : (tempColsFromValues as string[])

          setDetectedColumns(detectedCols)
        }

        toast({
          title: translate("Success"),
          description: translate(
            `SKN file processed successfully. Found ${response.data.rows || data.length} rows`,
          ),
          variant: "success",
        })
      } else {
        throw new Error(translate("Invalid API response format"))
      }
    } catch (error: any) {
      toast({
        title: translate("Error"),
        description: translate("API Error: " + (error?.response?.data?.message || error?.message)),
        variant: "error",
      })

      setFile(undefined)
      return error
    }
  }

  const removeAttachment = () => {
    setFile(undefined)
    setExcelColumns([])
    setSheetData([])
    setMapping((pre) => ({ ...pre, File: "" }))
    setDetectedColumns([])
    setSknFileContent("")
  }

  const getSavedMapping = async () => {
    const response = await axios.get(
      FINANCIAL_SERVER_URL + MAPPING_GET_ENDPOINT + `?type=${mappingType}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data, refetch } = useQuery({
    queryKey: ["mappings", mappingType],
    queryFn: getSavedMapping,
  })

  const saveMapping = async (mapping: any) => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + MAPPING_ADD_ENDPOINT,
      {
        type: mappingType,
        mapping,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const handleSaveMappingMutation = useMutation({
    mutationKey: ["mapping"],
    mutationFn: saveMapping,
    onSuccess: () => {
      setSubmittedMapping(true)
      refetch()
    },
  })

  const onSubmit = (mapping: DictType) => {
    setMapping({ ...mapping, Name: priceListName, File: file })
    const formattedMappings = Object.fromEntries(
      Object.entries(mapping).map(([key, value]) => [
        key.charAt(0).toLocaleLowerCase() + key.slice(1),
        value,
      ]),
    )

    handleSaveMappingMutation.mutate(formattedMappings)
  }

  const canConfirmMapping =
    Object.values(schemaValues).filter(Boolean).length >= expectedSchema.length

  const checkSavedDetectedMatch = useCallback(
    (detected: string[], saved: string[]) => {
      const detectedSet = new Set(detected)

      const status = saved.every((key) => detectedSet.has(key))

      if (status) {
        setSubmittedMapping(true)

        return true
      }

      setSubmittedMapping(false)

      return false
    },
    [file],
  )

  useEffect(() => {
    if (data) {
      const savedMapping = data?.mapping
      const savedMappingValues: string[] = Object.values(savedMapping)

      const matchingMapping = checkSavedDetectedMatch(detectedColumns, savedMappingValues)

      if (matchingMapping) {
        const formattedMappings: any = Object.fromEntries(
          Object.entries(savedMapping).map(([key, value]) => [
            key.charAt(0).toUpperCase() + key.slice(1),
            value,
          ]),
        )
        setSchemaValues(formattedMappings)

        setMapping({ ...formattedMappings, Name: priceListName, File: file })
      } else {
        expectedSchema.forEach((key) => {
          setSchemaValues((prevState) => ({ ...prevState, [key]: "" }))
        })
      }
    } else {
      expectedSchema.forEach((key) => {
        setSchemaValues((prevState) => ({ ...prevState, [key]: "" }))
      })
    }
  }, [expectedSchema, mappingType, data, file, detectedColumns])

  return (
    <div className='flex h-fit w-full flex-col overflow-hidden py-2'>
      {!file && <PriceListTemplateDownLoad boq={!!boq} />}

      {/* Upload Type Selection */}
      {!file && (
        <div className={cn('mb-4 space-y-3 w-full', isHebrew ? "rtl" :"ltr")}>
          <Label className='text-base font-medium'>{translate("Select Upload Type")}</Label>
          <RadioGroup
            value={uploadType}
            onValueChange={(value: "excel" | "skn") => setUploadType(value)}
            className={cn('flex gap-6', isHebrew ? "rtl" : "ltr")}
          >
            <div className='flex items-center space-x-2'>
              <RadioGroupItem value='excel' id='excel-upload' />
              <Label htmlFor='excel-upload' className='cursor-pointer text-sm font-medium'>
                {translate("Excel Upload")}
              </Label>
            </div>
            <div className={cn('flex items-center', isHebrew ? "!space-x-reverse" : "space-x-2")}>
              <RadioGroupItem value='skn' id='skn-upload' />
              <Label htmlFor='skn-upload' className='cursor-pointer text-sm font-medium'>
                {translate("SKN/ TLV File Upload")}
              </Label>
            </div>
          </RadioGroup>
        </div>
      )}

      {!file && (
        <div
          className={`flex h-96 items-center justify-center rounded-lg border-2 border-dashed p-6 text-center ${
            isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className='flex flex-col items-center justify-center space-y-2'>
            <div className='rounded-full bg-muted p-2'>
              <Upload className='h-6 w-6 text-muted-foreground' />
            </div>
            <h3 className='text-base font-medium'>
              {translate("Drag file here or click to upload")}{" "}
            </h3>
            <p className='text-sm text-muted-foreground'>
              {uploadType === "excel"
                ? translate("Support for XLSX & XLS")
                : translate("support for text files (.txt, .csv, .skn, .tlv)")}
            </p>

            <div className='flex w-full flex-col items-start justify-start space-y-2'>
              <Input
                type='file'
                id='file-upload'
                className='hidden'
                required
                onChange={(e) => {
                  if (e.target.files) {
                    setFile(e.target.files)
                    if (uploadType === "excel") {
                      onFileUpload(e)
                    } else {
                      onSknFileUpload(e)
                    }
                  }
                }}
                accept={uploadType === "excel" ? ".xls,.xlsx" : ".skn,.tlv"}
              />
            </div>

            {!file && (
              <label
                htmlFor='file-upload'
                className='inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50'
              >
                {translate("Select File")}
              </label>
            )}
          </div>
        </div>
      )}

      {file && (
        <div className={cn(isHebrew ? "rtl" : "ltr")}>
          <div className='flex flex-col pb-2'>
            <div className='flex w-full justify-between'>
              <Label className='capitalize'>{translate("Name")}</Label>
            </div>
            <Input
              defaultValue={translate(priceListName)}
              placeholder={boq ? translate("Enter BoQ Name") : translate("Enter price list name")}
              onChange={(e) => setPriceListName(e.target.value)}
              required
              className='text-black'
            />
          </div>

          {!previewExcel && !submittedMapping && (
            <div className='space-y-2 py-2'>
              <h4 className='text-sm font-semibold'>{translate("Detected Columns")}</h4>

              <div className='flex flex-wrap gap-2'>
                {detectedColumns.map((col: string, index) => (
                  <Badge key={index}>
                    {col.includes(`__EMPTY`) ? translate("Empty") + ` ${index + 1}` : col}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className='flex justify-end gap-2'>
            {submittedMapping && (
              <div
                onClick={() => setSubmittedMapping(false)}
                className='flex cursor-pointer items-center justify-center text-sm text-primary'
                aria-label='Update mapping'
              >
                <Pen className='mx-1' size={14} />
                <span>{translate("Update Mapping")}</span>
              </div>
            )}
            <div
              onClick={() => {
                removeAttachment()
                setSubmittedMapping(false)
              }}
              className='flex cursor-pointer items-center justify-center text-sm text-destructive'
              aria-label='Remove file'
            >
              <span>{translate("Replace File")}</span>
              <X className='mx-1' size={14} />
            </div>
          </div>

          {detectedColumns.length >= expectedPLSchema.length &&
            detectedColumns?.filter((col) => col.includes("__EMPTY")).length > 2 && (
              <Alert variant={"destructive"} className='my-1 text-destructive'>
                {translate("Remove the invalid cells/column heads to continue")}. <br />
                {translate("Or download the template to proceed")}.
              </Alert>
            )}

          <Separator className='my-2' />
          {detectedColumns.length >= expectedPLSchema.length &&
            detectedColumns?.filter((col) => col.includes("__EMPTY")).length < 2 &&
            !submittedMapping && (
              <div className='my-3 px-0.5'>
                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                  {expectedSchema?.map((field) => (
                    <div key={field}>
                      <Label className='mb-1 block'>{translate(field)}</Label>

                      <Select
                        value={schemaValues[`${field}`] || " 0"}
                        onValueChange={(e) => {
                          setSchemaValues((prev) => ({ ...prev, [field]: e }))
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={"Select Column"} />
                        </SelectTrigger>
                        <SelectContent>
                          {detectedColumns.map((col, index) => (
                            <SelectItem
                              key={col}
                              value={col}
                              disabled={col.includes(`__EMPTY`)}
                              className={cn(col.includes(`__EMPTY`) && "cursor-not-allowed")}
                            >
                              {col.includes(`__EMPTY`)
                                ? translate("Empty Cell") + ` ${index + 1}`
                                : col}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>

                <div className='flex flex-col items-end'>
                  {canConfirmMapping && (
                    <p className='w-full py-2 text-center text-sm text-primary'>
                      {translate("Confirm your column mappings to proceed")}
                    </p>
                  )}

                  <Button
                    type='submit'
                    className='mt-2 md:mt-0'
                    variant={canConfirmMapping ? "default" : "outline"}
                    size={"sm"}
                    onClick={() => onSubmit(schemaValues)}
                    disabled={!canConfirmMapping}
                  >
                    {translate("Save Mapping")}
                  </Button>
                  <Separator className='my-2' />
                </div>
              </div>
            )}

          {file && sheetData?.length > 0 && (
            <div className='mb-2'>
              <Button
                onClick={() => setPreviewExcel(!previewExcel)}
                variant={"outline"}
                type='button'
                className='flex w-full items-center gap-1 shadow-none outline-none'
              >
                <h3 className='font-semibold'>
                  {previewExcel ? translate("Collapse") : translate("Preview Excel")}
                </h3>
                {previewExcel ? <ChevronUp /> : <ChevronDown />}
              </Button>{" "}
            </div>
          )}

          {previewExcel && file && (
            <div className='h-auto max-h-[35vh] max-w-full overflow-auto rounded border md:max-w-fit'>
              <Table className='border-collapse text-sm'>
                <TableHeader className='bg-primary/40'>
                  <TableRow>
                    {excelColumns?.map((col, index) => (
                      <TableHead key={col + index} className='border px-2 py-1'>
                        {col.includes(`__EMPTY`) ? translate("") : col}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sheetData.map((row, i) => {
                    return (
                      <TableRow key={i}>
                        {excelColumns.map((col, index) => (
                          <TableCell key={col} className='min-w-6 border px-2 py-1'>
                            {row[col]}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const PriceListTemplateDownLoad = ({ boq }: { boq: boolean }) => {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const downloadTemplate = (language: string) => {
    const templateUrl = `${process.env.PUBLIC_URL}/assets/${boq ? "boq" : "price-list"}-template-${language}.xlsx`
    const link = document.createElement("a")
    link.href = templateUrl
    link.setAttribute("download", `${boq ? "boq" : "price-list"}-template-${language}.xlsx`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className='grid gap-4 px-0.5 pb-3'>
      <div>
        <Label>{translate("Use Template")}</Label>
        <div className='flex items-end gap-2'>
          <Select onValueChange={(value) => downloadTemplate(value)}>
            <SelectTrigger autoFocus className={isHebrew ? "rtl" : "ltr"} >
              <SelectValue className={isHebrew ? "rtl" : "ltr"} placeholder={translate("Download Template")} />
            </SelectTrigger>
            <SelectContent className={isHebrew ? "rtl" : "ltr"}>
              <SelectItem value='en'>{translate("English")}</SelectItem>
              <SelectItem value='he'>{translate("Hebrew")}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type='button'
            size='icon'
            variant='outline'
            className='h-9 w-9'
            onClick={() => downloadTemplate(locale)}
          >
            <Download className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  )
}
